import { z, uuidSchema } from '@kc3/validation';
import { RoutingStrategy } from '@kc3/types';
/** Asterisk maxlen / mod_callcenter max-wait-rows. 0 = unlimited. */
const maxCallers = z.number().int().min(0).max(10_000);
const name = z.string().min(1).max(64);
const url = z.string().url().max(2048);
const description = z.string().max(2048);
const maxWaitSeconds = z.number().int().min(1).max(86_400);
const abandonThresholdSec = z.number().int().min(0).max(3600);
const wrapUpSeconds = z.number().int().min(0).max(3600);
const ringProgressivelyDelaySec = z.number().int().min(1).max(120);
const tierWaitSeconds = z.number().int().min(0).max(3600);
const maxWaitNoAgentSec = z.number().int().min(0).max(86_400);
export const QueueRoutingStrategySchema = z.enum([
    RoutingStrategy.RING_ALL,
    RoutingStrategy.ROUND_ROBIN,
    RoutingStrategy.LEAST_RECENT,
    RoutingStrategy.FEWEST_CALLS,
    RoutingStrategy.SKILLS_BASED,
    RoutingStrategy.PRIORITY,
    RoutingStrategy.RING_PROGRESSIVELY,
    RoutingStrategy.LONGEST_IDLE_AGENT,
    RoutingStrategy.TOP_DOWN,
]);
export const QueueStatusSchema = z.enum(['active', 'deleted']);
export const CreateQueueRequestSchema = z.object({
    name,
    description: description.optional(),
    routingStrategy: QueueRoutingStrategySchema,
    maxWaitSeconds: maxWaitSeconds.optional(),
    musicOnHoldUrl: url.optional(),
    ivrMenuId: uuidSchema.optional(),
    announcePosition: z.boolean().optional(),
    announceEta: z.boolean().optional(),
    maxCallers: maxCallers.optional(),
    abandonThresholdSec: abandonThresholdSec.optional(),
    wrapUpSeconds: wrapUpSeconds.optional(),
    ringProgressivelyDelaySec: ringProgressivelyDelaySec.optional(),
    requiredSkillIds: z.array(uuidSchema).optional(),
    tierRulesApply: z.boolean().optional(),
    tierRuleWaitSeconds: tierWaitSeconds.optional(),
    tierRuleWaitMultiplyLevel: z.boolean().optional(),
    tierRuleNoAgentNoWait: z.boolean().optional(),
    maxWaitNoAgentSec: maxWaitNoAgentSec.optional(),
    maxWaitNoAgentTimeReachedSec: maxWaitNoAgentSec.optional(),
    // Omnichannel fields
    isOmnichannel: z.boolean().optional(),
    channels: z.array(z.string()).optional(),
    maxConcurrentInteractions: z.number().int().min(0).optional(),
    teamId: uuidSchema.nullable().optional(),
    campaignId: uuidSchema.nullable().optional(),
});
export const UpdateQueueRequestSchema = z
    .object({
    name: name.optional(),
    description: description.nullable().optional(),
    routingStrategy: QueueRoutingStrategySchema.optional(),
    maxWaitSeconds: maxWaitSeconds.optional(),
    musicOnHoldUrl: url.nullable().optional(),
    ivrMenuId: uuidSchema.nullable().optional(),
    announcePosition: z.boolean().optional(),
    announceEta: z.boolean().optional(),
    maxCallers: maxCallers.optional(),
    abandonThresholdSec: abandonThresholdSec.optional(),
    wrapUpSeconds: wrapUpSeconds.optional(),
    ringProgressivelyDelaySec: ringProgressivelyDelaySec.nullable().optional(),
    requiredSkillIds: z.array(uuidSchema).optional(),
    tierRulesApply: z.boolean().optional(),
    tierRuleWaitSeconds: tierWaitSeconds.optional(),
    tierRuleWaitMultiplyLevel: z.boolean().optional(),
    tierRuleNoAgentNoWait: z.boolean().optional(),
    maxWaitNoAgentSec: maxWaitNoAgentSec.optional(),
    maxWaitNoAgentTimeReachedSec: maxWaitNoAgentSec.optional(),
    // Omnichannel fields
    isOmnichannel: z.boolean().optional(),
    channels: z.array(z.string()).nullable().optional(),
    maxConcurrentInteractions: z.number().int().min(0).optional(),
    teamId: uuidSchema.nullable().optional(),
    campaignId: uuidSchema.nullable().optional(),
})
    .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'At least one field must be provided',
});
export const AddMemberRequestSchema = z.object({
    agentId: uuidSchema,
    priority: z.number().int().min(0).max(32_767).optional(),
    penalty: z.number().int().min(0).max(32_767).optional(),
    tierLevel: z.number().int().min(1).max(10).optional(),
    tierPosition: z.number().int().min(1).max(99).optional(),
    maxInteractionCount: z.number().int().min(0).optional(),
    status: z.boolean().optional(),
});
export const QueueResponseSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    description: z.string().nullable(),
    routingStrategy: z.string(),
    maxWaitSeconds: z.number().int(),
    musicOnHoldUrl: z.string().nullable(),
    status: QueueStatusSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    deletedAt: z.string().datetime().nullable(),
    // Omnichannel fields
    isOmnichannel: z.boolean().optional(),
    channels: z.array(z.string()).nullable().optional(),
    maxConcurrentInteractions: z.number().int().nullable().optional(),
    teamId: uuidSchema.nullable().optional(),
    campaignId: uuidSchema.nullable().optional(),
});
export const ListQueuesQuerySchema = z.object({
    includeDeleted: z
        .string()
        .optional()
        .transform((v) => v === 'true'),
    routingStrategy: QueueRoutingStrategySchema.optional(),
    search: z.string().optional(),
});
//# sourceMappingURL=queues.js.map